import { useEffect, useMemo, useState } from 'react'

import Modal from 'react-modal'

import { apiGetTokenInfo } from '@src/api/asset.v2.api'
import SearchIcon from '@src/components/icons/Search'
import { CHAIN_IDS, ChainId, ChainName, getChainNameFromChainId } from '@src/configs/chainInfo'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import {
    ChainSelector,
    SearchingIndicator,
    TokenCategoriesDisplay,
    TokenResultsDisplay,
} from '@src/pages/Exchange/components/SelectTokenModal/components'
import {
    filterBySelectedToken,
    filterTokensByQuery,
    sortTokensByPriority,
} from '@src/pages/Exchange/components/SelectTokenModal/utils'
import { useAvailableTokens } from '@src/pages/Exchange/hooks/useAvailableTokens'
import { IExchangeData } from '@src/types/exchange'
import { Address, IToken, SolanaAddress, TronAddress } from '@src/types/token'
import { getChecksumAddress, isEVMAddress, isSolanaAddress, isTronAddress } from '@src/utils/address'
import { Balance } from '@src/utils/balance'
import { isFeatureEnabled } from '@src/utils/environment'
import { useTokensStore } from '@src/zustand_stores'

interface ISelectTokenModalProps {
    isOpen: boolean
    onClose: () => void
    availableChains: Record<string, any>
    selectedChain: string | number
    setSelectedChain: (__chainId: string | number) => void
    selectedToken?: IToken
    setSelectedToken: (token: IToken) => void
    disabledChains?: number[]
    isSolanaSwap: boolean
    side: 'buy' | 'sell'
    buyData: IExchangeData
    sellData: IExchangeData
}

const SelectTokenModal = ({
    isOpen,
    onClose,
    availableChains,
    selectedChain,
    setSelectedChain,
    selectedToken,
    setSelectedToken,
    disabledChains = [],
    isSolanaSwap,
    side,
    buyData,
    sellData,
}: ISelectTokenModalProps): JSX.Element => {
    const [activeChainSelection, setActiveChainSelection] = useState<number>()
    const [tokenQuery, setTokenQuery] = useState('')
    const [searchResultsChain, setSearchResultsChain] = useState<Record<string, any>>({})
    const [searchResult, setSearchResult] = useState<IToken[]>([])
    const [highlightedChain, setHighlightedChain] = useState<number>()
    const [isSearchingAllChains, setIsSearchingAllChains] = useState(false)
    const [searchProgress, setSearchProgress] = useState({ completed: 0, total: 0 })

    const { tokens: storedTokens } = useTokensStore()
    const { isTronConnected, isSolanaConnected, isEVMConnected } = useWalletConnections()

    const isMantleEnabled = isFeatureEnabled('ENABLE_MANTLE_CHAIN')
    const isCeloEnabled = isFeatureEnabled('ENABLE_CELO_CHAIN')

    // Apply chain filtering based on feature flags and selected tokens
    const availableChainsFiltered = useMemo(() => {
        // Start with base filtering for Mantle chain
        let filteredChains = isMantleEnabled
            ? availableChains
            : Object.fromEntries(
                  Object.entries(availableChains).filter(([chainId]) => Number(chainId) !== ChainId.Mantle)
              )

        if (!isCeloEnabled) {
            filteredChains = Object.fromEntries(
                Object.entries(filteredChains).filter(([chainId]) => Number(chainId) !== ChainId.Celo)
            )
        }

        // Same-chain Solana swaps are now always allowed

        // Filter out Bitcoin chain if ENABLE_FEATHER is disabled
        if (!isFeatureEnabled('ENABLE_FEATHER')) {
            filteredChains = Object.fromEntries(
                Object.entries(filteredChains).filter(([chainId]) => Number(chainId) !== ChainId.Bitcoin)
            )
        }

        return filteredChains
    }, [availableChains, isMantleEnabled, isCeloEnabled])

    const getTokenBalance = (token: IToken) => {
        if (!token.userBalance || !token.userBalanceInUSD) return undefined

        // Only show balances for connected wallets on their respective chains
        if (
            (token.chainId === ChainId.Tron && !isTronConnected) ||
            (token.chainId === ChainId.Solana && !isSolanaConnected) ||
            (!isTronConnected && !isSolanaConnected && !isEVMConnected)
        ) {
            return undefined
        }

        // Format balance with reasonable precision
        const balance = new Balance(token).setValue(token.userBalance)
        const formattedBalance = balance.toNumber()

        // For small balances (less than 0.01), show more decimals to avoid showing 0
        const tokenBalance =
            formattedBalance < 0.0001 ? `<0.0001 ${token.symbol}` : `${formattedBalance.toFixed(4)} ${token.symbol}`

        // Format USD value with exactly 4 decimal places
        const usdValue = token.userBalanceInUSD
        const formattedUSD = usdValue < 0.01 ? '<$0.01' : usdValue < 1 ? usdValue.toFixed(4) : usdValue.toFixed(2)

        return {
            tokenBalance: tokenBalance,
            usdBalance: formattedUSD,
        }
    }

    const onChainClicked = (chainId: number): void => {
        // Update both the selected chain and active chain selection
        setSelectedChain(chainId)
        setActiveChainSelection(chainId)
        setHighlightedChain(chainId)

        // If we have an active token search, don't clear it
        // This allows viewing the searched token on the specific chain
        if (!tokenQuery) {
            setTokenQuery('')
        }
    }

    const onTokenClicked = (token: IToken): void => {
        // Ensure we update both the chain and token selection
        setSelectedChain(token.chainId)
        setSelectedToken(token)
        handleClose(token)
    }

    const handleClose = (token?: IToken): void => {
        // Clear all settings when modal is closed
        setActiveChainSelection(undefined)
        setTokenQuery('')
        setHighlightedChain(undefined)
        setSearchResult([])
        setSearchResultsChain({})

        if (token) {
            // Make sure we update both chain and token before closing
            setSelectedToken(token)
            setSelectedChain(token.chainId)
        }

        onClose()
    }

    // Use the active chain selection for filtering if available, otherwise fall back to selected chain
    const { tokens } = useAvailableTokens(activeChainSelection ?? selectedChain, isSolanaSwap, side === 'buy')

    const searchTokenByAddress = async (address: string) => {
        setIsSearchingAllChains(true)

        // Determine which networks to search based on address format
        let networksToSearch: ChainName[] = []
        if (isSolanaAddress(address)) {
            networksToSearch = ['solana']
        } else if (isTronAddress(address)) {
            networksToSearch = ['tron']
        } else if (isEVMAddress(address)) {
            // Search all EVM networks
            networksToSearch = Object.keys(CHAIN_IDS).filter(
                network =>
                    CHAIN_IDS[network as ChainName] !== ChainId.Solana &&
                    CHAIN_IDS[network as ChainName] !== ChainId.Tron
            ) as ChainName[]
        } else {
            // Invalid address format
            setIsSearchingAllChains(false)
            return
        }

        setSearchProgress({ completed: 0, total: networksToSearch.length })

        const results: IToken[] = []
        const foundChains: Record<string, any> = {}

        // Create an array of promises to fetch token info for each network in parallel
        const tokenInfoPromises = networksToSearch.map(async networkName => {
            try {
                const chainId = CHAIN_IDS[networkName]

                // Skip disabled chains, including Celo when it's in disabledChains
                if (disabledChains.includes(chainId)) {
                    // Update progress
                    setSearchProgress(prev => ({ completed: prev.completed + 1, total: prev.total }))
                    return null
                }

                const tokenInfo = await apiGetTokenInfo(getChainNameFromChainId(chainId), address)

                // Update progress after each request completes
                setSearchProgress(prev => ({ completed: prev.completed + 1, total: prev.total }))

                if (tokenInfo) {
                    let tokenAddress = address
                    if (chainId === ChainId.Solana) {
                        tokenAddress = address as SolanaAddress
                    } else if (chainId === ChainId.Tron) {
                        tokenAddress = address as TronAddress
                    } else {
                        // For EVM chains, use checksum address for the logo URL
                        tokenAddress = getChecksumAddress(address)
                    }

                    // Return token info if valid
                    if (!disabledChains.includes(chainId) && availableChainsFiltered[chainId]) {
                        return {
                            tokenInfo,
                            chainId,
                            tokenAddress,
                            networkName,
                        }
                    }
                }
            } catch (error) {
                console.error(`Error fetching token info for network ${networkName}:`, error)
                // Update progress even on error
                setSearchProgress(prev => ({ completed: prev.completed + 1, total: prev.total }))
            }

            return null
        })

        // Wait for all requests to complete in parallel
        const tokenResults = await Promise.all(tokenInfoPromises)

        // Process the results
        tokenResults.forEach(result => {
            if (result) {
                const { tokenInfo, chainId, tokenAddress } = result

                // Add the chain to foundChains
                foundChains[chainId] = availableChainsFiltered[chainId]

                // Add token to results
                results.push({
                    symbol: tokenInfo.symbol,
                    name: tokenInfo.name,
                    address: address as Address,
                    decimals: tokenInfo.decimals,
                    chainId: chainId,
                    // Use Trust Wallet repository for token logos
                    logoURI: `https://raw.githubusercontent.com/trustwallet/assets/refs/heads/master/blockchains/${getChainNameFromChainId(chainId)}/assets/${tokenAddress}/logo.png`,
                    native: false,
                    wrapped: false,
                    isStableCoin: false,
                })
            }
        })

        // Filter results before setting them
        const filteredResults = filterBySelectedToken(results, selectedToken, side)

        setSearchResult(filteredResults)
        // Update the searchResultsChain with only the chains where filtered tokens were found
        const filteredChains = Object.entries(foundChains).reduce(
            (filtered, [id, chain]) => {
                if (filteredResults.some(token => token.chainId === Number(id))) {
                    filtered[id] = chain
                }
                return filtered
            },
            {} as Record<string, any>
        )
        setSearchResultsChain(filteredChains)
        setIsSearchingAllChains(false)
    }

    const displayedTokens = useMemo(() => {
        // Initial filtering based on selected token
        const filteredTokens = filterBySelectedToken(tokens, selectedToken, side)

        // Same-chain Solana swaps are now always allowed

        if (!tokenQuery) return filteredTokens

        // Check if the input looks like a token address
        const trimmedQuery = tokenQuery.trim()
        if (
            trimmedQuery &&
            (isEVMAddress(trimmedQuery) || isSolanaAddress(trimmedQuery) || isTronAddress(trimmedQuery))
        ) {
            return [] // Return empty array while searching for token address
        }

        // Use the common filter function to search across tokens
        return filterTokensByQuery(filteredTokens, trimmedQuery, selectedToken, side)
    }, [tokens, tokenQuery, storedTokens, activeChainSelection, selectedToken, side, disabledChains])

    const onTokenSearchChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const newValue = e.target.value

        // Clear chain selection and results only when starting a new search or clearing an existing search
        if ((tokenQuery && !newValue) || (!tokenQuery && newValue)) {
            setActiveChainSelection(undefined)
            setHighlightedChain(undefined)
        }

        setTokenQuery(newValue)
        setSearchResultsChain({})

        // Check if the input looks like a token address
        const trimmedQuery = newValue.trim()
        if (
            trimmedQuery &&
            (isEVMAddress(trimmedQuery) || isSolanaAddress(trimmedQuery) || isTronAddress(trimmedQuery))
        ) {
            await searchTokenByAddress(trimmedQuery)
        } else if (trimmedQuery) {
            // For name-based search, use our helper function
            let filteredTokens = filterTokensByQuery(storedTokens, trimmedQuery, selectedToken, side)

            // Same-chain Solana swaps are now always allowed

            // Filter out tokens from disabled chains
            filteredTokens = filteredTokens.filter(token => !disabledChains.includes(token.chainId))

            // Get unique chains that have matching tokens
            const relevantChainIds = Array.from(new Set(filteredTokens.map(token => token.chainId)))
            const filteredChains = Object.entries(availableChainsFiltered).reduce(
                (filtered, [id, chain]) => {
                    if (relevantChainIds.includes(Number(id))) {
                        filtered[id] = chain
                    }
                    return filtered
                },
                {} as Record<string, any>
            )

            // Sort tokens using our helper function
            const sortedTokens = sortTokensByPriority(filteredTokens, trimmedQuery)

            setSearchResultsChain(filteredChains)
            setSearchResult(sortedTokens)
            setIsSearchingAllChains(false)
        } else {
            // Clear results if search is empty
            setSearchResult([])
            setSearchResultsChain({})
            setIsSearchingAllChains(false)
        }
    }

    // Modal styles (shared between mobile and desktop)
    const modalStyle = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--token-modal-overlay)',
            zIndex: 50,
            padding: '16px',
        },
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            width: '95%',
            maxWidth: '576px',
            height: '680px',
            padding: 0,
            border: '1px solid var(--token-modal-border)',
            borderWidth: '1px',
            borderRadius: '0.375rem',
            background: 'var(--token-modal-background)',
            overflow: 'hidden',
        },
    }

    return (
        <Modal isOpen={isOpen} onRequestClose={() => handleClose(undefined)} style={modalStyle} ariaHideApp={false}>
            {/* Mobile View */}
            <div className='flex h-full flex-col sm:hidden'>
                {/* Mobile Header */}
                <div className='flex items-center justify-between p-4'>
                    <h2 className='text-[20px] font-semibold'>Select asset</h2>
                    <button
                        onClick={onClose}
                        className='text-white/70 transition-colors hover:text-white'
                        type='button'
                    >
                        <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path
                                d='M18 6L6 18M6 6L18 18'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </svg>
                    </button>
                </div>

                {/* Mobile Search */}
                <div className='px-4 pb-4'>
                    <div className='flex w-full items-center gap-2 rounded-md border border-token-modal-border bg-token-modal-input-bg px-3 py-2.5 transition-all duration-200'>
                        <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                        <input
                            type='text'
                            placeholder='Search by symbol, name or address'
                            value={tokenQuery}
                            onChange={onTokenSearchChange}
                            className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none'
                        />
                    </div>
                </div>

                {/* Mobile Chains Section */}
                <div className='px-4 pb-3'>
                    <h3 className='mb-2 text-[20px] font-semibold'>Chains</h3>
                    <div className='grid grid-cols-4 gap-1.5'>
                        <ChainSelector
                            chains={tokenQuery ? searchResultsChain : availableChainsFiltered}
                            selectedChain={selectedChain}
                            onChainClicked={onChainClicked}
                            disabledChains={disabledChains}
                            highlightedChain={highlightedChain}
                            tokenQuery={tokenQuery}
                            activeChainSelection={activeChainSelection}
                            isMobile={true}
                        />
                    </div>
                </div>

                {/* Mobile Assets Section */}
                <div className='flex-1 overflow-y-auto px-4'>
                    <h3 className='mb-4 text-[20px] font-semibold'>Assets</h3>
                    {isSearchingAllChains ? (
                        <SearchingIndicator searchProgress={searchProgress} />
                    ) : tokenQuery ? (
                        <TokenResultsDisplay
                            activeChainSelection={activeChainSelection}
                            tokenQuery={tokenQuery}
                            availableChains={availableChainsFiltered}
                            displayedTokens={displayedTokens}
                            getTokenBalance={getTokenBalance}
                            selectedToken={selectedToken}
                            onTokenClicked={onTokenClicked}
                            searchResult={searchResult}
                        />
                    ) : (
                        <TokenCategoriesDisplay
                            displayedTokens={displayedTokens}
                            selectedToken={selectedToken}
                            onTokenClicked={onTokenClicked}
                            getTokenBalance={getTokenBalance}
                        />
                    )}
                </div>
            </div>

            {/* Desktop View */}
            <div className='hidden h-full flex-col sm:flex'>
                {/* New Header Structure with Chains and Assets */}
                <div className='flex'>
                    <div className='w-[192px] border-r border-token-modal-divider-dark p-4'>
                        <h2 className='text-[20px] font-semibold'>Chains</h2>
                    </div>
                    <div className='flex flex-1 items-center justify-between p-4'>
                        <h2 className='text-[20px] font-semibold'>Assets</h2>
                        <button
                            onClick={onClose}
                            className='text-white/70 transition-colors hover:text-white'
                            type='button'
                        >
                            <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M18 6L6 18M6 6L18 18'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className='flex min-h-0 flex-1 flex-col sm:flex-row'>
                    {/* Left panel - Chains */}
                    <div className='w-[192px] flex-col border-r border-token-modal-divider-dark'>
                        <div className='no-scrollbar h-full overflow-y-auto'>
                            <div className='space-y-1.5 px-3 pt-1'>
                                <ChainSelector
                                    chains={tokenQuery ? searchResultsChain : availableChainsFiltered}
                                    selectedChain={selectedChain}
                                    onChainClicked={onChainClicked}
                                    disabledChains={disabledChains}
                                    highlightedChain={highlightedChain}
                                    tokenQuery={tokenQuery}
                                    activeChainSelection={activeChainSelection}
                                    isMobile={false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right panel - Tokens with Search */}
                    <div className='flex min-h-0 w-full flex-1 flex-col'>
                        {/* Search Bar */}
                        <div className='px-3 pt-1'>
                            <div className='flex w-full items-center gap-2 rounded-md border border-token-modal-border bg-token-modal-input-bg px-3 py-2.5 transition-all duration-200'>
                                <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                                <input
                                    type='text'
                                    placeholder='Search by symbol, name or address'
                                    value={tokenQuery}
                                    onChange={onTokenSearchChange}
                                    className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none'
                                />
                            </div>
                        </div>

                        <div className='h-full overflow-y-auto'>
                            {isSearchingAllChains ? (
                                <SearchingIndicator searchProgress={searchProgress} />
                            ) : tokenQuery ? (
                                <div className='p-3'>
                                    <TokenResultsDisplay
                                        activeChainSelection={activeChainSelection}
                                        tokenQuery={tokenQuery}
                                        availableChains={availableChainsFiltered}
                                        displayedTokens={displayedTokens}
                                        getTokenBalance={getTokenBalance}
                                        selectedToken={selectedToken}
                                        onTokenClicked={onTokenClicked}
                                        searchResult={searchResult}
                                    />
                                </div>
                            ) : (
                                <div className='p-3'>
                                    <TokenCategoriesDisplay
                                        displayedTokens={displayedTokens}
                                        selectedToken={selectedToken}
                                        onTokenClicked={onTokenClicked}
                                        getTokenBalance={getTokenBalance}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default SelectTokenModal
